<template>
  <div v-trust-us-view-tracker :class="['trust-us', { 'swiper-main-container': notDesktop }]">
    <LegacyWrap>
      <h2 class="trust-us__header">{{ t('title') }}</h2>
      <div v-if="!notDesktop" class="trust-us__theses">
        <Card
          v-for="(these, index) in theses"
          :key="index"
          :title="these.title"
          :subtitle="these.subtitle"
          :img="these.img"
        />
      </div>
    </LegacyWrap>

    <UiSlider v-if="notDesktop" v-bind="cardProps" class="swiper">
      <template #skeleton>
        <SkeletonItem
          :skeleton-style="{
            width: '100%',
            height: '322px',
            borderRadius: '16px',
          }"
          :is-animated="true"
        />
      </template>
      <template #slide="{ slide }">
        <Card v-bind="toThesis(slide)" />
      </template>
      <template #rightArrowIcon>
        <IconBase name="right-arrow" color="--alias-color-icon-subtle" />
      </template>
      <template #leftArrowIcon>
        <IconBase class="mirror" name="right-arrow" color="--alias-color-icon-subtle" />
      </template>
    </UiSlider>
  </div>
</template>

<script lang="ts" setup>
import { SkeletonItem } from '@experience/core-skeleton-lib';
import { UiSlider } from '@experience/core-slider-ui';
import { useI18n } from '@experience/i18n';
import { computed } from 'vue';

import IconBase from '@/shared/icons/IconBase.vue';
import LegacyWrap from '@/shared/LegacyWrap.vue';
import { useAnalytics } from '@/shared/lib/analytics';
import { type CarouselConfig } from '@/shared/models/slider';
import { useScreenSize } from '@/shared/utils/breakpoints';

import ru from './trustUs/i18n/ru';
import Card from './trustUs/TrustUsCard.vue';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { useViewTracker } = useAnalytics();
const breakpoints = useScreenSize();

interface Thesis {
  title: string;
  subtitle: string;
  img: string;
}

const theses = computed<Thesis[]>(() => [
  {
    title: t('theses.refund.title'),
    subtitle: t('theses.refund.subtitle'),
    img: 'money',
  },
  {
    title: t('theses.support.title'),
    subtitle: t('theses.support.subtitle'),
    img: 'heart',
  },
  {
    title: t('theses.askGuide.title'),
    subtitle: t('theses.askGuide.subtitle'),
    img: 'chat',
  },
]);

const toThesis = (slide: unknown): Thesis => slide as Thesis;

const notDesktop = computed(() => breakpoints.isTablet.value || breakpoints.isMobile.value);

const cardProps = computed(() => {
  const params: CarouselConfig<Thesis> = {
    spaceBetween: 8,
    slides: theses.value,
    options: { initialSlide: 0, slidesOffsetBefore: 20, slidesOffsetAfter: 20 },
    cardWidth: 322,
  };

  return params;
});

const vTrustUsViewTracker = useViewTracker('main:usp.view');
</script>

<style lang="scss" scoped>
.trust-us__header {
  @include typography.base-800-750-extra;

  @include adaptivity.tablet {
    text-align: left;
  }

  @include adaptivity.mobile {
    @include typography.base-700-500-extra;
  }
}

.trust-us__theses {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  width: 100%;
}

.swiper,
.trust-us__theses {
  margin-top: 32px;

  @include adaptivity.tablet {
    margin-top: 24px;
  }

  @include adaptivity.mobile {
    margin-top: 20px;
  }
}
</style>
